import React, { useState, useContext, useEffect } from "react";
import { WithChildren } from '../../../../_metronic/helpers/react18MigrationHelpers';
import { tableParamsInterface } from '../../../components/table/interfaces'
import { serviceConfigInterface } from '../interfaces/serviceRunsInterfaces'
import { useLazyConnectDBQuery } from "../../../modules/ServiceRuns/redux/cloudAppAPI";
import { GridFilterModel, GridLogicOperator } from "@mui/x-data-grid-pro";

export interface searchFilterObjectInterface {
  daterange: [Date | null, Date | null],
  searchText: string
}
type GridSortDirection = 'asc' | 'desc' | null | undefined

type ServiceRunsContextInterface = {
  filterObject: searchFilterObjectInterface,
  setFilterObject: React.Dispatch<React.SetStateAction<searchFilterObjectInterface>>,
  expandedGroups: Set<string>;
  setExpandedGroups: React.Dispatch<React.SetStateAction<Set<string>>>,
  tableParams: tableParamsInterface,
  setTableParams: React.Dispatch<React.SetStateAction<tableParamsInterface>>,
  currentTableItems: number[],
  setCurrentTableItems: React.Dispatch<React.SetStateAction<number[]>>,
  currentServiceConfig: serviceConfigInterface,
  setCurrentServiceConfig: React.Dispatch<React.SetStateAction<serviceConfigInterface>>,
  setCommonQueries: React.Dispatch<React.SetStateAction<{ [key: string]: { [key: string]: string; }[]; } | undefined>>,
  commonQueries: { [key: string]: { [key: string]: string; }[]; } | undefined,
  trigger: (par: {}) => ({}),
  result: any,
  errorMessage: string,
  setErrorMessage: React.Dispatch<React.SetStateAction<string>>,
  queryTrigger: boolean,
  setQueryTrigger: React.Dispatch<React.SetStateAction<boolean>>,
  setPaginationModel: React.Dispatch<React.SetStateAction<{page: number, pageSize: number}>>,
  setSortModelData: React.Dispatch<React.SetStateAction<{field: string, sort: GridSortDirection}[]>>,
  sortModelData: { field: string, sort: GridSortDirection }[],
  paginationModel: {page: number, pageSize: number},
  setEditTable: React.Dispatch<React.SetStateAction<boolean>>,
  editTable: boolean,
  setNewRow: React.Dispatch<React.SetStateAction<boolean>>,
  newRow: boolean,
  setTableContentReady: React.Dispatch<React.SetStateAction<boolean>>,
  tableContentReady: boolean,
  filterModel: GridFilterModel,
  setFilterModel: React.Dispatch<React.SetStateAction<GridFilterModel>>,
  filteredRowIds: number[],
  setFilteredRowIds: React.Dispatch<React.SetStateAction<number[]>>,
  columns:{ field: string, headerName: string, sortable: boolean, flex: number }[], 
  setColumns:React.Dispatch<React.SetStateAction<{ field: string, headerName: string, sortable: boolean, flex: number }[]>>,
  records:{ [key: string]: string | number}[], 
  initialRecords: { [key: string]: string }[],
  setInitialRecords:React.Dispatch<React.SetStateAction<{ [key: string]: string }[]>>,
  setRecords:React.Dispatch<React.SetStateAction<{ [key: string]: string | number }[]>>,
  setAutoCompleteBlur: React.Dispatch<React.SetStateAction<string>>,
  autoCompleteBlur: string,
  fetchingParams: string, 
  setFetchingParams: React.Dispatch<React.SetStateAction<string>>,
  tableErrors: { [key: string]: string | number }[],
  setTableErrors: React.Dispatch<React.SetStateAction<{ [key: string]: string | number}[]>>
  tableValuesObject: { [key: string]: string }, 
  setTableQueryValuesObject: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>
  getTableData: boolean, 
  setGetTableData:React.Dispatch<React.SetStateAction<boolean>>
  tableFieldName: {service: string, name: string} | "", 
  setTableFieldName:React.Dispatch<React.SetStateAction<{service: string, name: string} | "">>
}

const ServiceRunsContext = React.createContext<ServiceRunsContextInterface>({
  filterObject: {
    searchText: "",
    daterange: [null, null]
  },
  expandedGroups: new Set(),
  setExpandedGroups: () => new Set(),
  tableParams: {
    pageNumber: 1,
    pageSize: 10,
    sortOrder: 'desc',
    sortField: 'registered'
  },
  currentTableItems: [],
  setCurrentTableItems: () => { },
  setTableParams: () => { },
  setFilterObject: () => { },
  currentServiceConfig: { service_descriptive_name: "", allowed_groups: "", field_config: [], page_topic: "", action_buttons_config: [], buConfig: [{ name: "Choose BU", value: "default" }] },
  setCurrentServiceConfig: () => { },
  setCommonQueries: () => { },
  commonQueries: {},
  trigger: (par: {}) => ({}),
  result: {},
  errorMessage: "",
  setErrorMessage: () => { },
  queryTrigger: false,
  setQueryTrigger: () => { },
  setPaginationModel: () => { },
  setSortModelData: () => { },
  sortModelData: [],
  paginationModel:{page: 0, pageSize: 10},
  setEditTable: () => { },
  editTable: false,
  setNewRow: () => { },
  newRow: false,
  filterModel: { items: []},
  setFilterModel: () => { },
  filteredRowIds: [],
  setFilteredRowIds: () => {},
  columns: [], 
  setColumns: () => {},
  records: [], 
  setRecords: () => {},
  initialRecords: [],
  setInitialRecords: () => {},
  setAutoCompleteBlur: () => {},
  autoCompleteBlur: "",
  fetchingParams: "", 
  setFetchingParams: () => {},
  setTableContentReady: () => {},
  tableContentReady: false,
  tableErrors: [],
  setTableErrors: () => {},
  tableValuesObject: {}, 
  setTableQueryValuesObject: () => {},
  getTableData: false, 
  setGetTableData:() => {},
  tableFieldName: "", 
  setTableFieldName:() => {},
});

export function useServiceRunsContext() {
  return useContext(ServiceRunsContext);
}

export const ServiceRunsProvider: React.FC<WithChildren> = (props) => {
  const [filterObject, setFilterObject] = useState<searchFilterObjectInterface>({
    searchText: "",
    daterange:[new Date(new Date().setDate(new Date().getDate() - 1)), new Date()]
  })
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [currentTableItems, setCurrentTableItems] = useState<number[]>([])
  const [tableParams, setTableParams] = useState<tableParamsInterface>({
    pageNumber: 1,
    pageSize: 10,
    sortOrder: 'desc',
    sortField: 'registered'
  })
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10
  });
  const [sortModelData, setSortModelData] = useState<{field: string, sort: GridSortDirection}[]>([]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
    logicOperator: "and" as GridLogicOperator,
  });
  const [filteredRowIds, setFilteredRowIds] = useState<number[]>([]);
  const [currentServiceConfig, setCurrentServiceConfig] = useState<serviceConfigInterface>({service_descriptive_name:"", allowed_groups:"", field_config:[], page_topic:"", action_buttons_config: [],buConfig: [{ name: "Choose BU", value: "default" }]})
  const [commonQueries, setCommonQueries] = useState<{ [key: string]: { [key: string]: string; }[]; } | undefined>()
  const [trigger, result] = useLazyConnectDBQuery();
  const [errorMessage, setErrorMessage] = useState("");
  const [queryTrigger, setQueryTrigger] = useState(false);
  const [editTable, setEditTable] = useState<boolean>(false);
  const [newRow, setNewRow] = useState(false);
  const [tableContentReady, setTableContentReady] = useState(false);
  const [columns, setColumns] = useState<{ field: string, headerName: string, sortable: boolean, flex: number }[]>([]);
  const [lastService, setLastService] = useState('');
  const [lastFirstField, setLastFirstField] = useState('');
  const [records, setRecords] = useState<{ [key: string]: string | number }[]>([]);
  const [initialRecords, setInitialRecords] = useState<{ [key: string]: string }[]>([]);
  const [autoCompleteBlur, setAutoCompleteBlur] = useState("");
  const [fetchingParams, setFetchingParams] = useState("");
  const [tableErrors, setTableErrors] = useState<{ [key: string]: string | number }[]>([]);
  const [tableValuesObject, setTableQueryValuesObject] = useState<{ [key: string]: string }>({});
  const [getTableData, setGetTableData] = useState(false);
  const [tableFieldName, setTableFieldName] = useState<{service: string, name: string} | "">("");

  // useEffect(() => {
  //   if((currentServiceConfig.service_descriptive_name !== lastService && columns[0]?.field && lastFirstField !== columns[0]?.field) || (sortModelData.length === 0 && columns[0]?.field)) {
  //     setSortModelData([{ field: columns[0].field, sort: 'desc' as GridSortDirection }]);
  //     setLastService(currentServiceConfig.service_descriptive_name);
  //     setLastFirstField(columns[0]?.field)
  //   }
  // }, [currentServiceConfig.service_descriptive_name, columns])
  
  return <ServiceRunsContext.Provider
    value={{
      filterObject,
      setFilterObject,
      expandedGroups,
      setExpandedGroups,
      tableParams,
      setTableParams,
      currentTableItems,
      setCurrentTableItems,
      currentServiceConfig,
      setCurrentServiceConfig,
      setCommonQueries,
      commonQueries,
      trigger,
      result,
      errorMessage,
      setErrorMessage,
      queryTrigger,
      setQueryTrigger,
      setPaginationModel,
      setSortModelData,
      sortModelData,
      paginationModel,
      setEditTable,
      editTable,
      setNewRow,
      newRow,
      setFilterModel,
      filterModel,
      filteredRowIds,
      setFilteredRowIds,
      columns, 
      setColumns,
      setRecords,
      records,
      initialRecords, 
      setInitialRecords,
      setAutoCompleteBlur,
      autoCompleteBlur,
      fetchingParams, 
      setFetchingParams,
      tableContentReady, 
      setTableContentReady,
      tableErrors, 
      setTableErrors,
      tableValuesObject, 
      setTableQueryValuesObject,
      getTableData, 
      setGetTableData,
      tableFieldName, 
      setTableFieldName
    }}>
    {props.children}
  </ServiceRunsContext.Provider>
}

export default ServiceRunsContext;